import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import '../styles/DisplayPage.css'; // Ensure your CSS file path is correct

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const socket = io(BASE_URL);

// --- Translation Dictionary (for UI text) ---
const translations = {
  en: {
    queueStatus: "Queue Status",
    nowServing: "Now Serving",
    waitingQueue: "Waiting Queue",
    lastCompleted: "Last Completed",
    skippedTokens: "Skipped Tokens",
    noOneWaiting: "No one is waiting!",
    noRecentCompletions: "No recent completions.",
    noSkippedTokens: "No tokens have been skipped.",
    noTokensServing: "No tokens currently being served."
  },
  my: { // Burmese translations - Adjust as needed
    queueStatus: "တန်းစီ အခြေအနေ",
    nowServing: "ဝန်ဆောင်မှု ပေးနေသည်",
    waitingQueue: "စောင့်ဆိုင်းနေသော တန်းစီနံပါတ်များ",
    lastCompleted: "နောက်ဆုံး ပြီးစီးခဲ့သည်များ",
    skippedTokens: "ကျော်သွားသော တန်းစီနံပါတ်များ",
    noOneWaiting: "စောင့်ဆိုင်းသူ မရှိပါ!",
    noRecentCompletions: "မကြာသေးမီက ပြီးစီးခဲ့သည်များ မရှိပါ။",
    noSkippedTokens: "ကျော်သွားသော တန်းစီနံပါတ်များ မရှိပါ။",
    noTokensServing: "လက်ရှိ ဝန်ဆောင်မှု ပေးနေသော တန်းစီနံပါတ် မရှိပါ။"
  }
};

// --- Helper for English SpeechSynthesis Text (NO AUDIO FILES NEEDED FOR THIS) ---
// This function determines the English text to speak for each part of the token number.
const getEnglishSpeechText = (type, value) => {
    switch(type) {
        case 'phrase_token_number': return "Token number";
        case 'phrase_at_counter': return "at counter";
        case 'phrase_serving': return "is now being served.";
        case 'letter': return `${value}. `;  // e.g., "A"
        case 'digit': return value;  // e.g., "0", "1", "2"
        default: return "";
    }
};

// --- Centralized Audio Playback Manager ---
// This object manages the state and logic for sequential audio announcements.
const audioPlaybackManager = (() => {
    let _announcementQueue = [];         // Queue of pending announcements { audioData, totalRepeats }
    let _currentPlayingClip = null;      // The current HTMLAudioElement instance OR SpeechSynthesisUtterance
    let _currentAnnouncementState = null; // State of the announcement being actively played
                                         // { audioData, totalRepetitions, currentRepetitionIndex, clipIndexInRep, compiledClipsForOneRep }
    let _isProcessingQueue = false;      // Flag to prevent multiple processQueue calls simultaneously
    let _debounceTimeout = null;         // For debouncing incoming socket events before adding to queue

    // Helper to compile the array of audio playback instructions for one full announcement
    // Each item in the compiled list will be { type: 'url', value: 'path/to/clip.m4a' } or { type: 'tts', value: 'Text to speak' }
    const compileAnnouncementParts = (audioData, lang) => {
        const parts = [];
        // Use window.location.origin for correct base URL (e.g., http://localhost:3000)
        // Then append /static/audio/my/ to get to the Burmese audio files
        //const audioBaseMy = `${window.location.origin}/static/audio/my/`; 
        const audioBaseMy = `${BASE_URL}/static/audio/my/`; 

        if (!audioData || !audioData.token_number || !audioData.token_digits || !audioData.counter_number) {
            console.warn("compileAnnouncementParts: Invalid or incomplete audioData provided.", audioData);
            return [];
        }
        
        const { token_prefix, token_digits, counter_number } = audioData;

        // --- Logic to select between pre-recorded (Burmese) or TTS (English) ---
        if (lang === 'my') { // Burmese: Use pre-recorded files
            parts.push({ type: 'url', value: `${audioBaseMy}phrase_token_number.m4a` });
            if (token_prefix) {
              parts.push({ type: 'url', value: `${audioBaseMy}letter_${token_prefix}.m4a` });
            }
            token_digits.forEach(digit => {
              parts.push({ type: 'url', value: `${audioBaseMy}digit_${digit}.m4a` });
            });
            parts.push({ type: 'url', value: `${audioBaseMy}phrase_at_counter.m4a` });
            String(counter_number).split('').forEach(digit => {
              parts.push({ type: 'url', value: `${audioBaseMy}digit_${digit}.m4a` });
            });
            parts.push({ type: 'url', value: `${audioBaseMy}phrase_serving.m4a` });
        } else { // English: Use browser TTS
            // Example: "Token number A 0 0 1 at counter 1 is now being served."
            parts.push({ type: 'tts', value: getEnglishSpeechText('phrase_token_number') });
            if (token_prefix) {
              parts.push({ type: 'tts', value: getEnglishSpeechText('letter', token_prefix) });
            }
            token_digits.forEach(digit => {
              parts.push({ type: 'tts', value: getEnglishSpeechText('digit', digit) });
            });
            parts.push({ type: 'tts', value: getEnglishSpeechText('phrase_at_counter') });
            String(counter_number).split('').forEach(digit => {
              parts.push({ type: 'tts', value: getEnglishSpeechText('digit', digit) });
            });
            parts.push({ type: 'tts', value: getEnglishSpeechText('phrase_serving') });
        }
        
        return parts;
    };

    // Plays the next single part (clip or TTS) in the current announcement's sequence
    const playNextPartInSequence = () => {
        // Clear any previous audio instance (important for smooth transitions)
        if (_currentPlayingClip) {
            // If it's a SpeechSynthesisUtterance, cancel it
            if (_currentPlayingClip instanceof SpeechSynthesisUtterance) {
                window.speechSynthesis.cancel();
            } 
            // If it's an HTMLAudioElement, pause and clear its source
            else if (_currentPlayingClip instanceof HTMLAudioElement) {
                _currentPlayingClip.pause();
                _currentPlayingClip.src = '';
            }
            _currentPlayingClip = null;
        }

        if (!_currentAnnouncementState) {
            console.log("AudioPlayer: No active announcement state. Trying to process queue.");
            _isProcessingQueue = false; 
            processAnnouncementQueue(); 
            return;
        }

        const { compiledPartsForOneRep, currentRepetitionIndex, totalRepetitions } = _currentAnnouncementState;

        // If all parts for the current repetition are played
        if (_currentAnnouncementState.partIndexInRep >= compiledPartsForOneRep.length) {
            if (currentRepetitionIndex < totalRepetitions - 1) {
                // More repetitions needed
                _currentAnnouncementState.currentRepetitionIndex++;
                _currentAnnouncementState.partIndexInRep = 0; // Reset part index for next rep
                console.log(`AudioPlayer: Starting repetition ${_currentAnnouncementState.currentRepetitionIndex + 1}/${totalRepetitions}`);
                
                setTimeout(() => { // Short pause before starting next repetition
                    playNextPartInSequence(); // Restart the sequence from the beginning
                }, 700); // 0.7 second pause between repetitions
            } else {
                // All repetitions done. Clear current announcement state.
                console.log(`AudioPlayer: Finished all announcements for token ${_currentAnnouncementState.audioData.token_number}.`);
                _currentAnnouncementState = null;
                _isProcessingQueue = false; // Mark queue as not processing
                processAnnouncementQueue(); // Try to process the next announcement in queue
            }
            return; // Stop further processing for this call
        }

        // Get the current part (either URL or TTS text)
        const currentPart = compiledPartsForOneRep[_currentAnnouncementState.partIndexInRep];

        // Play the part based on its type
        if (currentPart.type === 'url') {
            _currentPlayingClip = new Audio(currentPart.value);
            _currentPlayingClip.volume = 1.0;

            // Capture current state values for the callbacks (closure)
            const partIndex = _currentAnnouncementState.partIndexInRep; 
            const announcementId = _currentAnnouncementState.audioData.id;

            _currentPlayingClip.onended = () => {
                // Only proceed if still the active announcement and this part
                if (_currentAnnouncementState && _currentAnnouncementState.audioData.id === announcementId && _currentAnnouncementState.partIndexInRep === partIndex) {
                    _currentAnnouncementState.partIndexInRep++; // Move to the next part
                    playNextPartInSequence(); // Play the next part
                } else {
                    console.log("AudioPlayer: onended fired for old part, state changed.");
                }
            };
            _currentPlayingClip.onerror = (e) => {
                console.error("Audio playback error:", e, "URL:", currentPart.value);
                if (_currentAnnouncementState && _currentAnnouncementState.audioData.id === announcementId && _currentAnnouncementState.partIndexInRep === partIndex) {
                    _currentAnnouncementState.partIndexInRep++; // Try next part even on error
                    playNextPartInSequence();
                } else {
                    console.log("AudioPlayer: onerror fired for old part, state changed.");
                }
            };
            _currentPlayingClip.play().catch(error => {
                console.error("Autoplay prevented or other error (HTMLAudioElement):", error);
            });
        } else if (currentPart.type === 'tts') {
            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(currentPart.value);
                utterance.lang = 'en-US'; // Explicitly set English language for TTS (can be dynamic if needed)
                // You can set other properties like pitch, rate, volume here if desired
                
                _currentPlayingClip = utterance; // Store the utterance

                const partIndex = _currentAnnouncementState.partIndexInRep; 
                const announcementId = _currentAnnouncementState.audioData.id;

                utterance.onend = () => {
                    // Only proceed if still the active announcement and this part
                    if (_currentAnnouncementState && _currentAnnouncementState.audioData.id === announcementId && _currentAnnouncementState.partIndexInRep === partIndex) {
                        _currentAnnouncementState.partIndexInRep++; // Move to the next part
                        playNextPartInSequence(); // Play the next part
                    } else {
                        console.log("AudioPlayer: TTS onend fired for old part, state changed.");
                    }
                };
                utterance.onerror = (e) => {
                    console.error("Speech synthesis error:", e);
                    if (_currentAnnouncementState && _currentAnnouncementState.audioData.id === announcementId && _currentAnnouncementState.partIndexInRep === partIndex) {
                        _currentAnnouncementState.partIndexInRep++; // Try next part even on error
                        playNextPartInSequence();
                    } else {
                        console.log("AudioPlayer: TTS onerror fired for old part, state changed.");
                    }
                };
                window.speechSynthesis.speak(utterance);
            } else {
                console.warn("Speech synthesis not supported in this browser. Skipping TTS part.");
                _currentAnnouncementState.partIndexInRep++; // Move to next part
                playNextPartInSequence();
            }
        }
    };

    // Processes the announcement queue, ensuring only one announcement plays at a time
    const processAnnouncementQueue = () => {
        if (_isProcessingQueue || _currentAnnouncementState) {
            // Already processing or an announcement is active, wait for it to finish.
            return;
        }

        if (_announcementQueue.length === 0) {
            // Queue is empty, nothing to do.
            _isProcessingQueue = false;
            return;
        }

        _isProcessingQueue = true; // Mark as processing
        const nextAnnouncement = _announcementQueue.shift(); // Get next announcement from queue

        // Prepare the state for the announcement
        // --- Pass the language to compileAnnouncementParts ---
        const compiledParts = compileAnnouncementParts(nextAnnouncement.audioData, nextAnnouncement.lang); 
        if (compiledParts.length === 0) { // If parts couldn't be compiled, skip this announcement
            console.warn("Skipping announcement due to empty compiled parts list.");
            _isProcessingQueue = false;
            processAnnouncementQueue(); // Try next in queue
            return;
        }

        _currentAnnouncementState = {
            audioData: nextAnnouncement.audioData,
            totalRepetitions: nextAnnouncement.totalRepeats,
            currentRepetitionIndex: 0,
            partIndexInRep: 0, // Reset part index
            compiledPartsForOneRep: compiledParts // Store the compiled parts for this sequence
        };

        console.log(`AudioPlayer: Starting new announcement for token ${_currentAnnouncementState.audioData.token_number} in language ${nextAnnouncement.lang}.`);
        playNextPartInSequence(); // Start playing the first part of this new announcement
    };

    // Public method to add an announcement to the queue
    // Now takes 'lang' parameter for audio
    const addAnnouncementToQueue = (audioData, repeats = 2, lang = 'en') => {
        // Debounce incoming requests for the same token to prevent queue flooding
        if (_debounceTimeout) {
            clearTimeout(_debounceTimeout); // Clear previous debounce
        }

        _debounceTimeout = setTimeout(() => {
            console.log(`AudioPlayer: Adding token ${audioData.token_number} (${lang}) to queue with ${repeats} repeats.`);
            _announcementQueue.push({ audioData, totalRepeats: repeats, lang: lang }); // Store lang with queue item
            _debounceTimeout = null; // Clear timeout
            processAnnouncementQueue(); // Attempt to process queue
        }, 100); // Debounce delay
    };

    // Public method to clear the current announcement and queue
    const stopAndClear = () => {
        if (_currentPlayingClip) {
            if (_currentPlayingClip instanceof SpeechSynthesisUtterance) {
                window.speechSynthesis.cancel(); // Cancel TTS
            } else if (_currentPlayingClip instanceof HTMLAudioElement) {
                _currentPlayingClip.pause(); // Pause HTMLAudioElement
                _currentPlayingClip.src = '';
            }
            _currentPlayingClip = null;
        }
        _announcementQueue = [];
        _currentAnnouncementState = null;
        _isProcessingQueue = false;
        if (_debounceTimeout) {
            clearTimeout(_debounceTimeout);
            _debounceTimeout = null;
        }
        console.log("AudioPlayer: All announcements stopped and queue cleared.");
    };

    return {
        add: addAnnouncementToQueue,
        stop: stopAndClear
    };
})();


// --- React Functional Component: DisplayPage ---
export default function DisplayPage() {
  // --- Language State ---
  // Initialize language from local storage, default to English if not found
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('displayLanguage') || 'en';
  });
  const t = translations[language]; // Get current translations
  const { tenantId } = useParams(); // <<< GET tenantId FROM URL >>>
  const [activeCalledTokens, setActiveCalledTokens] = useState([]);
  const [waitingTokens, setWaitingTokens] = useState([]);
  const [lastCompletedTokens, setLastCompletedTokens] = useState([]);
  const [skippedTokens, setSkippedTokens] = useState([]); // State for skipped tokens
  const [message, setMessage] = useState(''); // State for error message

  // --- Data Fetching Function ---
  const fetchData = useCallback(() => {
    // --- VALIDATE tenantId from URL ---
    if (!tenantId) {
      console.error("DisplayPage: Tenant ID missing in URL. Cannot fetch data.");
      setMessage("Tenant ID is missing in the URL. Please use a URL like /display/123.");
      return;
    }

    // --- Construct URL with tenantId as query parameter for ALL API calls ---
    const apiParams = `?tenant_id=${tenantId}`;

    axios.get(`${BASE_URL}/api/tokens/active-called${apiParams}`) // <<< Append tenantId
      .then(res => setActiveCalledTokens(res.data))
      .catch(err => {
        console.error("Error fetching active called tokens:", err);
        setActiveCalledTokens([]);
      });

    axios.get(`${BASE_URL}/api/tokens/waiting${apiParams}`) // <<< Append tenantId
      .then(res => setWaitingTokens(res.data))
      .catch(err => {
        console.error("Error fetching waiting tokens:", err);
        setWaitingTokens([]);
      });

    axios.get(`${BASE_URL}/api/tokens/last-completed/3${apiParams}`) // <<< Append tenantId
      .then(res => setLastCompletedTokens(res.data))
      .catch(err => {
        console.error("Error fetching last completed tokens:", err);
        setLastCompletedTokens([]);
      });

    axios.get(`${BASE_URL}/api/tokens/skipped${apiParams}`) // <<< Append tenantId
      .then(res => setSkippedTokens(res.data))
      .catch(err => {
        console.error("Error fetching skipped tokens:", err);
        setSkippedTokens([]);
      });

  }, [tenantId, language]); // Dependency includes tenantId
  
  const fetchDataEOD = () => {
    // Fetch currently served tokens
    axios.get(`${BASE_URL}/api/tokens/active-called`)
      .then(res => setActiveCalledTokens(res.data))
      .catch(() => setActiveCalledTokens([]));

    // Fetch waiting tokens
    axios.get(`${BASE_URL}/api/tokens/waiting`)
      .then(res => setWaitingTokens(res.data))
      .catch(() => setWaitingTokens([]));

    // Fetch last 3 completed tokens
    axios.get(`${BASE_URL}/api/tokens/last-completed/0`)
      .then(res => setLastCompletedTokens(res.data))
      .catch(() => setLastCompletedTokens([]));

    // Fetch skipped tokens
    axios.get(`${BASE_URL}/api/tokens/skipped`)
      .then(res => setSkippedTokens(res.data))
      .catch(() => setSkippedTokens([]));
  };


  // --- Language Change Handler ---
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('displayLanguage', lang); // Persist selection
    // Optional: Stop current audio and clear queue if language changes mid-announcement
    audioPlaybackManager.stop(); 
  };

  // --- useEffect Hook for component lifecycle ---
  useEffect(() => {
    fetchData(); // Initial data load when component mounts

    // --- Socket.IO Event Listeners ---
    socket.on('new_token', fetchData); // Refresh data when a new token is created

    socket.on('update_token', (token) => {
      console.log("DisplayPage: Received 'update_token' event. Full Token data:", token);
      fetchData(); // Always refetch all data to keep UI updated

      // --- Trigger Audio Playback based on token status and data ---
      // Add announcement to queue if token is 'called' AND has valid audio_data.
      if (token.status === "called" && token.audio_data && token.audio_data.token_number) { 
        console.log("DisplayPage: Token is 'called' and has audio_data. Adding to audio queue.");
        // Add to queue with 2 repetitions, passing the CURRENTLY SELECTED language
        audioPlaybackManager.add(token.audio_data, 2, language); // Pass 'language' state
      }
      else {
        // If token status changes away from 'called' (e.g., served, skipped),
        // and it was the token being announced, stop its audio and clear state.
        console.log("DisplayPage: Token not 'called' or missing audio_data. No audio triggered.");
        // If the token that was just announced changes status (e.g., served or skipped)
        // ensure its announcement is stopped.
        if (audioPlaybackManager._currentAnnouncementState && 
            token.id === audioPlaybackManager._currentAnnouncementState.audioData.id &&
            token.status !== "called") {
             console.log(`DisplayPage: Token ${token.id} status changed to ${token.status}. Stopping current announcement.`);
             audioPlaybackManager.stop(); // Stop any ongoing audio and clear the queue/state
        } else {
            console.log("DisplayPage: Token not 'called' or missing audio_data. No audio triggered. (Not current announcement)");
        }
      }
    });

    socket.on('eod_cleanup_completed', (data) => {
      console.log("DisplayPage: Received EOD completion event:", data);
      console.log("DisplayPage: EOD cleanup completed for this tenant. Refreshing data.");
      fetchDataEOD(); // Trigger a refresh of all token data
      
    });
    // Cleanup: Remove Socket.IO listeners when the component unmounts to prevent memory leaks.
    return () => {
      socket.off('new_token');
      socket.off('update_token');
      socket.off('eod_cleanup_completed');
      // Ensure any audio is stopped when component unmounts
      audioPlaybackManager.stop(); 
    };
  }, [language]); // Dependency array now includes 'language' to re-run useEffect when language changes

  // --- Component Render (JSX) ---
  return (
    <div className="container">
      <header className="header" style={{ position: 'relative' }}> {/* Make header relative for absolute positioning of buttons */}
        {t.queueStatus}
        {/* Language Selection Buttons */}
        <div style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)' }}>
          <button 
            onClick={() => handleLanguageChange('en')} 
            style={{ padding: '0.5rem 1rem', marginRight: '0.5rem', background: language === 'en' ? '#007bff' : '#ccc', color: language === 'en' ? 'white' : 'black', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            ENG
          </button>
          <button 
            onClick={() => handleLanguageChange('my')} 
            style={{ padding: '0.5rem 1rem', background: language === 'my' ? '#007bff' : '#ccc', color: language === 'my' ? 'white' : 'black', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            MY
          </button>
        </div>
      </header>

      <main className="main-content">
        {/* Left Section: Now Serving */}
        <section className="section now-serving">
          <h3 className="section-title">{t.nowServing}</h3>
          {activeCalledTokens.length === 0 ? (
            <div className="empty-state">{t.noTokensServing}</div>
          ) : (
            // Display all actively called tokens
            <div className="now-serving-cards"> {/* Wrapper for consistent centering/layout */}
              {activeCalledTokens.map(token => (
                <div key={token.id} className="token-card">
                  <span className="token-number">{token.token_number}</span>
                  <span className="counter-number">Counter: {token.counter_id}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Right Sections: Waiting Queue & Last Completed & Skipped */}
        <div className="queue-and-completed">
          {/* Waiting Queue Section */}
          <section className="section">
            <h3 className="section-title">{t.waitingQueue}</h3>
            {waitingTokens.length === 0 ? (
              <div className="empty-state">{t.noOneWaiting}</div>
            ) : (
              <div className="waiting-queue-grid"> {/* Use the new grid for responsive columns */}
                {/* Each token is a list-item in the grid */}
                {waitingTokens.map(token => (
                  <div key={token.id} className="list-item waiting-list-item">
                    <span className="list-item-number">{token.token_number}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Last Completed Section */}
          <section className="section">
            <h3 className="section-title">{t.lastCompleted}</h3> 
            {lastCompletedTokens.length === 0 ? (
              <div className="empty-state">{t.noRecentCompletions}</div>
            ) : (
              <div className="list-container"> {/* Use list-container for general list styling and scroll */}
                {lastCompletedTokens.map(token => (
                  <div key={token.id} className="list-item1 completed-list-item">
                    <span className="list-item-number">{token.token_number} at Counter: {token.counter_id}</span>
                    <span className="list-item-time">
                      ({token.status})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
          
          {/* Skipped Tokens Section */}
          <section className="section">
            <h3 className="section-title">{t.skippedTokens}</h3> 
            {skippedTokens.length === 0 ? (
              <div className="empty-state">{t.noSkippedTokens}</div>
            ) : (
              <div className="list-container"> {/* Use list-container for general list styling and scroll */}
                {skippedTokens.map(token => (
                  <div key={token.id} className="list-item1 skipped-list-item">
                    <span className="list-item-number">{token.token_number}</span>
                    <span className="list-item-time">
                      ({token.status})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
  